import { Users, User, Trash2, TrendingUp, CheckCircle } from 'lucide-react'
import { StatsCardSkeleton } from '../LoadingSkeleton'

export default function AdminStatsCards({ userStats, loadingStats, fetchUserStats }) {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800">User Statistics</h2>
        <button
          onClick={() => fetchUserStats()}
          disabled={loadingStats}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className={`w-4 h-4 ${loadingStats ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {loadingStats ? 'Refreshing...' : 'Refresh Stats'}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        {loadingStats ? (
          <>
            <StatsCardSkeleton />
            <StatsCardSkeleton />
            <StatsCardSkeleton />
            <StatsCardSkeleton />
            <StatsCardSkeleton />
          </>
        ) : (
          <>
            {/* Total Users Card */}
            <div className="relative backdrop-blur-xl bg-gradient-to-br from-blue-50/80 to-blue-50/80 p-6 rounded-2xl shadow-lg border border-white/40 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-blue-500/0 group-hover:from-blue-500/5 group-hover:to-blue-500/5 rounded-2xl transition-all duration-500"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-400/10 rounded-full blur-2xl group-hover:bg-blue-400/20 transition-all duration-500"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Users</p>
                  <p className="text-3xl font-bold text-blue-600">{userStats.totalUsers}</p>
                  <p className="text-xs text-gray-500 mt-1">All registered</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-md">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </div>
            </div>

            {/* Users Created Today Card */}
            <div className="relative backdrop-blur-xl bg-gradient-to-br from-green-50/80 to-emerald-50/80 p-6 rounded-2xl shadow-lg border border-white/40 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-emerald-500/0 group-hover:from-green-500/5 group-hover:to-emerald-500/5 rounded-2xl transition-all duration-500"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-green-400/10 rounded-full blur-2xl group-hover:bg-green-400/20 transition-all duration-500"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">New Today</p>
                  <p className="text-3xl font-bold text-green-600">{userStats.totalUsersCreated}</p>
                  <p className="text-xs text-gray-500 mt-1">Created today</p>
                </div>
                <div className="bg-green-100 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-md">
                  <User className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </div>

            {/* Online Now Card */}
            <div className="relative backdrop-blur-xl bg-gradient-to-br from-blue-50/80 to-pink-50/80 p-6 rounded-2xl shadow-lg border border-white/40 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-pink-500/0 group-hover:from-blue-500/5 group-hover:to-pink-500/5 rounded-2xl transition-all duration-500"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-400/10 rounded-full blur-2xl group-hover:bg-blue-400/20 transition-all duration-500"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-600">Online Now</p>
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                  <p className="text-3xl font-bold text-blue-600">{userStats.totalLoggedIn}</p>
                  <p className="text-xs text-gray-500 mt-1">Real-time active users</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-md">
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
              </div>
            </div>

            {/* Active Users Card */}
            <div className="relative backdrop-blur-xl bg-gradient-to-br from-orange-50/80 to-amber-50/80 p-6 rounded-2xl shadow-lg border border-white/40 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 to-amber-500/0 group-hover:from-orange-500/5 group-hover:to-amber-500/5 rounded-2xl transition-all duration-500"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-orange-400/10 rounded-full blur-2xl group-hover:bg-orange-400/20 transition-all duration-500"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Active Users</p>
                  <p className="text-3xl font-bold text-orange-600">{userStats.activeUsers}</p>
                  <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
                </div>
                <div className="bg-orange-100 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-md">
                  <CheckCircle className="w-8 h-8 text-orange-600" />
                </div>
              </div>
            </div>

            {/* Deleted Users Card */}
            <div className="relative backdrop-blur-xl bg-gradient-to-br from-red-50/80 to-rose-50/80 p-6 rounded-2xl shadow-lg border border-white/40 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-rose-500/0 group-hover:from-red-500/5 group-hover:to-rose-500/5 rounded-2xl transition-all duration-500"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-red-400/10 rounded-full blur-2xl group-hover:bg-red-400/20 transition-all duration-500"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Deleted Users</p>
                  <p className="text-3xl font-bold text-red-600">{userStats.deletedUsers}</p>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </div>
                <div className="bg-red-100 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-md">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
